import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Marketing-site top navigation.
 *
 * Ported from the HTML draft's `<nav class="nav">` markup. Internal nav
 * links use React Router's `<Link>` so SPA transitions don't full-reload
 * the page; the "See what's cooking →" CTA remains an external `<a>` to
 * the actual app at app.homeplated.com.
 *
 * Hamburger behavior (mobile breakpoint, controlled by site.css's
 * `.is-open` class on the nav and `.nav-open` on the body):
 *   - Click toggles open/closed; aria-expanded + aria-label kept in sync.
 *   - Escape closes the menu.
 *   - Clicking any nav link closes the menu (so SPA navigation lands on a
 *     fresh-paint state, not with the menu still draped over the page).
 *   - The CSS variable `--nav-h` is set from the nav's measured height
 *     and re-measured on resize, so the slide-down animation in site.css
 *     positions itself correctly.
 *
 * Replaces the vanilla-JS IIFEs in `assets/site.js` (now removed).
 */
export const SiteHeader: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // Keep --nav-h in sync with the nav's actual height; site.css uses it
  // to anchor the open-menu slide.
  useEffect(() => {
    const syncNavHeight = () => {
      if (!navRef.current) return;
      document.documentElement.style.setProperty(
        '--nav-h',
        `${navRef.current.offsetHeight}px`,
      );
    };
    syncNavHeight();
    window.addEventListener('resize', syncNavHeight);
    return () => window.removeEventListener('resize', syncNavHeight);
  }, []);

  // Mirror open state onto <body> (site.css uses .nav-open on body to lock
  // scroll while the menu is open). Effect handles both set + cleanup so
  // navigating away with the menu open doesn't leave the body locked.
  useEffect(() => {
    document.body.classList.toggle('nav-open', open);
    return () => {
      document.body.classList.remove('nav-open');
    };
  }, [open]);

  // Escape closes the menu.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Close the menu whenever the user navigates (covers React-Router-driven
  // route changes; the per-link onClick below covers same-page clicks too).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const closeOnClick = () => setOpen(false);

  return (
    <nav ref={navRef} className={`nav${open ? ' is-open' : ''}`}>
      <div className="container nav-inner">
        <Link to="/" className="nav-logo" aria-label="HomePlated home" onClick={closeOnClick}>
          <img src="/images/logo-orange.svg" alt="HomePlated" />
        </Link>
        <div className="nav-links" id="nav-links">
          <Link to="/chefs" onClick={closeOnClick}>For Chefs</Link>
          <Link to="/eaters" onClick={closeOnClick}>For Eaters</Link>
          <Link to="/how-it-works" onClick={closeOnClick}>How It Works</Link>
          <Link to="/ambassador" onClick={closeOnClick}>Ambassadors</Link>
          <Link to="/safety" onClick={closeOnClick}>Safety</Link>
          <Link to="/about" onClick={closeOnClick}>About</Link>
        </div>
        <a
          href={import.meta.env.VITE_APP_DOMAIN}
          target="_blank"
          rel="noopener"
          className="nav-cta"
        >
          See what&apos;s cooking →
        </a>
        <button
          className="nav-hamburger"
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="nav-links"
          onClick={() => setOpen((v) => !v)}
        >
          <svg className="icon-open" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          <svg className="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </nav>
  );
};
